import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import { GenzoTrackApplyFilterActionEventType } from '@pages/FlightSearchResult/types';
import { useContext } from 'react';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import { FlightSearchFiltersContext } from '../contexts/FlightSearchFiltersContext';
import useFlightSearchPageParams from './useFlightSearchPageParams';

const useFlightSearchFilters = () => {
  const { isMultiCity } = useFlightSearchPageParams();

  const flightSearchFilters = useContext(FlightSearchFiltersContext);

  const {
    toggleMultiCitySingleFilter,
    activeLeg,
    originAirportCodesFilter,
    destinationAirportCodesFilter,
  } = flightSearchFilters;

  const selectedOriginAirportsForActiveLeg =
    originAirportCodesFilter.find((item) => item.leg === activeLeg)?.values || [];

  const selectedDestinationAirportsForActiveLeg =
    destinationAirportCodesFilter.find((item) => item.leg === activeLeg)?.values || [];

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const { legFilterOptions } = useFlightSearchResults();

  const departLegFilterOption = isMultiCity
    ? legFilterOptions[flightSearchFilters.activeLeg]
    : legFilterOptions[0];

  const returnLegFilterOption = legFilterOptions[1];

  const handleOriginAirportCodeClick = (
    code: string,
    genzoTrackApplyFilterSearchView: () => void,
    genzoTrackApplyFilterActionEvent: GenzoTrackApplyFilterActionEventType,
  ) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      searchParamKey: FlightSearchResultSearchParams.Origin,
      value: code,
    });

    genzoTrackApplyFilterSearchView();

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      from_to_airports: newFilter[activeLeg].values.concat(selectedDestinationAirportsForActiveLeg),
    });
  };

  const handleDestinationAirportCodeClick = (
    code: string,
    genzoTrackApplyFilterSearchView: () => void,
    genzoTrackApplyFilterActionEvent: GenzoTrackApplyFilterActionEventType,
  ) => {
    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      searchParamKey: FlightSearchResultSearchParams.Destination,
      value: code,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      from_to_airports: selectedOriginAirportsForActiveLeg.concat(newFilter[activeLeg].values),
    });

    genzoTrackApplyFilterSearchView();
  };

  return {
    ...flightSearchFilters,
    departLegFilterOption,
    returnLegFilterOption,
    handleOriginAirportCodeClick,
    handleDestinationAirportCodeClick,
  };
};

export default useFlightSearchFilters;
